import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import { useAppSelector } from 'hooks';
import { Button, Popover, PopoverBody, PopoverHeader, Progress } from 'reactstrap';

import { BYTES_IN_KB, BYTES_IN_MB, MAX_UPLOAD_SIZE_BYTES, MAX_UPLOAD_SIZE_MB } from '~constants/common';

import { faCheck, faXmark } from '~components/icon';

import { selectUserTags } from '~main/selectors/login';

import { ClickableIcon } from './ClickableIcon';

interface ComponentProps {
  id: string;
  icon: any;
  toolTip?: string;
  accept?: string;
  multiple?: boolean;
  onUpload: (file: File, setProgress: (value: number) => void) => Promise<void>;
  onUploadError?: (message: string) => void;
  disabled?: boolean;
  maxSize?: number;
  externalError?: string | null;
  authTag?: string;
}

const ClickableUploadIcon: React.FC<ComponentProps> = ({
  id,
  icon,
  toolTip,
  accept = '.csv, application/json',
  multiple = false,
  onUpload,
  onUploadError,
  disabled,
  maxSize,
  externalError,
  authTag,
}) => {
  const tags = useAppSelector(selectUserTags);
  const visible = !authTag || (authTag && tags.includes(authTag));
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const iconRef = useRef<HTMLDivElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [internalErrorMessage, setInternalErrorMessage] = useState<string | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(false);

  const errorMessage = internalErrorMessage || externalError;

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleChange = ({ target }: ChangeEvent<HTMLInputElement>) => {
    const file = target.files?.[0] || null;
    target.value = '';

    setInternalErrorMessage(null);
    setSelectedFile(null);

    if (file) {
      const maxBytes = maxSize ?? MAX_UPLOAD_SIZE_BYTES;
      if (file.size > maxBytes) {
        setInternalErrorMessage(`File size cannot exceed ${maxSize ?? MAX_UPLOAD_SIZE_MB} MB`);
        return;
      }
    }
    setSelectedFile(file);
  };

  const handleConfirmUpload = async () => {
    if (!selectedFile) return;

    setIsUploading(true);
    setProgress(0);

    try {
      await onUpload(selectedFile, setProgress);
    } catch (error: any) {
      const message = typeof error === 'string' ? error : error?.message || 'Unable to upload file.';
      onUploadError?.(message);
    } finally {
      setIsUploading(false);
      setSelectedFile(null);
      setProgress(null);
    }
  };

  const formatFileSize = (sizeInBytes: number): string => {
    const kb = sizeInBytes / BYTES_IN_KB;
    const mb = sizeInBytes / BYTES_IN_MB;
    return sizeInBytes >= BYTES_IN_MB ? `${mb.toFixed(2)} MB` : `${kb.toFixed(2)} KB`;
  };

  // Show popover automatically when externalError is set
  useEffect(() => {
    if (errorMessage) setPopoverOpen(true);
  }, [errorMessage]);

  if (!visible) return null;

  return (
    <>
      <div className="d-flex flex-column align-items-start w-auto" ref={iconRef}>
        {!selectedFile && (
          <>
            <ClickableIcon
              id={id}
              icon={icon}
              onClick={!disabled ? handleClick : undefined}
              toolTip={toolTip}
              className={`${disabled ? 'opacity-50 pointer-events-none' : ''} ${externalError ? 'text-danger' : ''}`}
            />
            {errorMessage && (
              <Popover isOpen={popoverOpen} target={iconRef} placement="bottom">
                <PopoverHeader className="d-flex align-items-end justify-content-between text-dark">
                  Upload Error
                  <Button close onClick={() => setPopoverOpen(false)} />
                </PopoverHeader>
                <PopoverBody className="text-danger">{errorMessage}</PopoverBody>
              </Popover>
            )}
          </>
        )}
      </div>
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleChange}
        accept={accept}
        multiple={multiple}
      />
      {selectedFile && (
        <div className="nav d-flex flex-column align-items-start bg-light px-2 py-2 rounded w-auto">
          <div className="d-flex align-items-center w-100">
            <span className="me-2 flex-grow-1">
              {selectedFile.name} ({formatFileSize(selectedFile.size)})
            </span>
            {!isUploading && (
              <>
                <ClickableIcon
                  id="upload-file-cancel"
                  icon={faXmark}
                  onClick={() => {
                    setSelectedFile(null);
                    setInternalErrorMessage(null);
                  }}
                  color="danger"
                  toolTip="Cancel"
                />
                <ClickableIcon
                  id="upload-file-confirm"
                  icon={faCheck}
                  onClick={handleConfirmUpload}
                  color="success"
                  toolTip="Confirm"
                  className="ms-2"
                />
              </>
            )}
          </div>
          {isUploading && typeof progress === 'number' && (
            <Progress value={progress} animated className="w-100">
              {progress}%
            </Progress>
          )}
        </div>
      )}
    </>
  );
};

export default ClickableUploadIcon;
